import { Box, Typography } from '@mui/material';
import { Organization } from '_api/types';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddOrganization, OrganizationModal } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useMemo, useState } from 'react';
import { useOrganizations } from 'utils/hooks';

export const OrganizationManagement = () => {
  const { activeOrganizations, deleteOrganization, isLoading } = useOrganizations();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!activeOrganizations) return [];
    if (searchTerm.trim() === '') return activeOrganizations;
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return activeOrganizations.filter((org) =>
      org.name.toLowerCase().includes(lowercasedSearchTerm),
    );
  }, [searchTerm, activeOrganizations]);

  const [openModal, setModalOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | undefined>(undefined);
  const [openView, setOpenView] = useState(false);
  const [viewOrg, setViewOrg] = useState<Organization | undefined>(undefined);

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };

  const handleOpenViewModal = (row: Organization) => {
    setViewOrg(row);
    setOpenView(true);
  };

  const handleOpenEditModal = (row: Organization) => {
    setEditOrg(row);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditOrg(undefined);
  };

  const handleCloseView = () => {
    setOpenView(false);
    setViewOrg(undefined);
  };

  const handleArchiveOnClick = (orgID: number) => {
    deleteOrganization({ orgID });
  };

  const actionButtons: ViewButtons = {
    viewButton: true,
    editButton: true,
    archiveButton: true,
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const areaCode = phoneNumber.slice(0, 3);
    const firstThree = phoneNumber.slice(3, 6);
    const lastFour = phoneNumber.slice(6);
    return `(${areaCode})${firstThree}-${lastFour}`;
  };

  const tableColumns = useMemo<MRT_ColumnDef<Organization>[]>(
    () => [
      {
        header: 'Name',
        accessorFn: (row) => `${row.name}`,
      },
      {
        header: 'Address',
        enableSorting: false,
        accessorFn: (row) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
            <Typography>{row.streetAddress}</Typography>
            <Typography>{`${row.city}, ${row.state} ${row.zipCode}`}</Typography>
          </Box>
        ),
      },
      {
        header: 'Phone Number',
        enableSorting: false,
        accessorFn: (row) =>
          row && row.phoneNumber ? formatPhoneNumber(row.phoneNumber) : '',
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'organization'}
            viewOnClick={() => handleOpenViewModal(row)}
            archiveOnClick={() => handleArchiveOnClick(row.id)}
            editOnClick={() => handleOpenEditModal(row)}
            actionButtons={actionButtons}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          value={searchTerm}
          onAddClick={() => setModalOpen(true)}
          handleSearch={handleSearch}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddOrganization open={openModal} onClose={() => setModalOpen(false)} />
      {viewOrg != undefined ? (
        <OrganizationModal
          open={openView}
          organization={viewOrg}
          onClose={handleCloseView}
        />
      ) : null}
      {editOrg != undefined ? (
        <OrganizationModal
          isEdit={true}
          open={openEdit}
          organization={editOrg}
          onClose={handleCloseEdit}
        />
      ) : null}
    </Box>
  );
};
